/**
 * Host-derived environment helpers.
 *
 * Sibling hosts are built from the host the page was served from, never written down, so a link
 * always stays inside its own environment. There is deliberately no "are we in production?" test
 * here: the app behaves identically everywhere, which is what keeps lab a real rehearsal for
 * production rather than a different program.
 *
 * The one exception is the iOS app's build of this same code
 * (wip-archive/ios-app/IOS_CONTAINER_APP.md). Its page is served from the app's bundle at
 * capacitor://localhost, so there is no host to derive from, and the build supplies the
 * environment's domain instead, from the same config the stacks are deployed with.
 */

// Localhost is not an environment; it points at lab for anything it cannot serve itself.
const LAB_DOMAIN = "lab1.tingthings.app";

const PROD_HOSTS = new Set(["my.tingthings.app", "tingthings.app"]);

const PROD_DOMAIN = "tingthings.app";

/**
 * Whether this page is running inside the iOS app rather than in a browser. Set by the app's
 * build, never at runtime, so it cannot be spoofed by a page and costs the web build nothing.
 */
export function isNativeShell() {
  return import.meta.env.VITE_NATIVE === "1";
}

/**
 * Whether this page is production.
 *
 * Only the environment badge and the capture-spike log ($lib/captureprobe.js, /lab) use this.
 * Both observe and never alter behaviour — the app's behaviour is identical everywhere, and it
 * must stay that way or lab stops being a rehearsal for production. This answers `true` during
 * prerender so the badge is never baked into static markup; a non-production page adds it on
 * mount.
 */
export function isProdHost() {
  if (typeof window === "undefined") return true;
  if (isNativeShell()) return baseDomain() === PROD_DOMAIN;
  return PROD_HOSTS.has(window.location.hostname);
}

/**
 * The environment's base domain, with the subdomain the app is served from stripped:
 * `my.lab1.tingthings.app` -> `lab1.tingthings.app`, `my.tingthings.app` -> `tingthings.app`.
 *
 * Every sibling host (`cdn.`, `login.`) is built from this rather than written down, so a link
 * always stays inside the environment the page was served from. Building one by stripping the
 * prefix and going no further lands on the bare domain, which nothing serves.
 *
 * Localhost has no environment of its own, so it borrows lab's. Inside the iOS app the page is
 * on localhost too, but there the build says which environment it is.
 */
export function baseDomain() {
  if (isNativeShell()) return import.meta.env.VITE_NATIVE_DOMAIN;
  if (typeof window === "undefined") return LAB_DOMAIN;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return LAB_DOMAIN;
  return host.replace(/^(my|login)\./, "");
}

/** The host the app itself is served from, in whichever environment this page is. */
export function appHost() {
  return `my.${baseDomain()}`;
}

/** Where user-uploaded images and other CDN assets come from, per environment. */
export function cdnBase() {
  return `https://cdn.${baseDomain()}`;
}

/**
 * The origin API calls and photos are fetched from. On the web they are same-origin on the app's
 * own host, so this is empty and every URL stays relative. Inside the iOS app the page has no
 * host of its own, so they go to the app host of the environment the app was built for.
 */
export function apiBase() {
  if (isNativeShell()) return `https://${appHost()}`;
  return import.meta.env.VITE_API_URL || "";
}
